/**
 * Small utilities to work with task metadata across the HouseTaskManager
 * Focus on extracting common patterns like houseId/documentId and building keys
 */

import { Task, TaskStatus } from '@/types/tasks';
import { CONCURRENT_TASK_TYPES, ConcurrentTaskType } from './types';

export type AnyMeta = Record<string, unknown> | undefined | null;

/** Extracts houseId from task metadata supporting both houseId and house_id */
export function getHouseIdFromMeta(meta: AnyMeta): number | undefined {
	if (!meta) return undefined;
	const m = meta as Record<string, unknown>;
	return (m.houseId as number) ?? (m.house_id as number) ?? undefined;
}

/** Extracts documentId from task metadata */
export function getDocumentIdFromMeta(meta: AnyMeta): number | undefined {
	if (!meta) return undefined;
	const m = meta as Record<string, unknown>;
	return (m.documentId as number) ?? undefined;
}

/** Extracts filename from task metadata */
export function getFilenameFromMeta(meta: AnyMeta): string | undefined {
	if (!meta) return undefined;
	const m = meta as Record<string, unknown>;
	return (m.filename as string) ?? undefined;
}

/** Builds a stable delete key houseId:documentId from numbers */
export function makeDeleteKey(houseId?: number, documentId?: number): string | undefined {
	if (typeof houseId !== 'number' || typeof documentId !== 'number') return undefined;
	return `${houseId}:${documentId}`;
}

/** Builds a stable delete key from metadata */
export function makeDeleteKeyFromMeta(meta: AnyMeta): string | undefined {
	return makeDeleteKey(getHouseIdFromMeta(meta), getDocumentIdFromMeta(meta));
}

/** Status helpers (avoid inline string comparisons) */
export function isStatusActive(status: TaskStatus): boolean {
	return status === TaskStatus.Queued || status === TaskStatus.InProgress || status === TaskStatus.Pending;
}

export function isStatusTerminal(status: TaskStatus): boolean {
	return (
		status === TaskStatus.Completed ||
		status === TaskStatus.Failed ||
		status === TaskStatus.Skipped ||
		status === TaskStatus.Cancelled ||
		status === TaskStatus.Paused
	);
}

/** Stopped because an allowance is spent rather than because anything went wrong. */
export function isStatusPaused(status: TaskStatus): boolean {
	return status === TaskStatus.Paused;
}

/**
 * Whether another attempt at this task could reach a different outcome. Completed and skipped are
 * answers, not shortfalls — only an attempt that never landed is worth repeating.
 */
export function isStatusWorthRetrying(status: TaskStatus): boolean {
	// Paused included: by the time a folder is dropped again the allowance may have refilled, and
	// trying is the only way to find out.
	return status === TaskStatus.Failed || status === TaskStatus.Cancelled || status === TaskStatus.Paused;
}

export function isTaskActive(task: Task): boolean {
	return isStatusActive(task.status);
}

/** Task type guards (avoid inline type comparisons) */
export function isTaskUpload(obj: { taskType: ConcurrentTaskType }): boolean {
	return obj.taskType === CONCURRENT_TASK_TYPES.UPLOAD_RECORD;
}

export function isTaskDelete(obj: { taskType: ConcurrentTaskType }): boolean {
	return obj.taskType === CONCURRENT_TASK_TYPES.DELETE_RECORD;
}

export function isTaskUpdate(obj: { taskType: ConcurrentTaskType }): boolean {
	return obj.taskType === CONCURRENT_TASK_TYPES.UPDATE_RECORD;
}
